import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';

const GalleryPage = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // BATHROOMS - Primary Focus (Strongest First)
  const bathroomImages = [
    {
      id: 'b1',
      image: '/gallery/bathroom-luxury-spa.webp',
      title: 'Luxury Spa Bathroom',
      description: 'Premium spa-style bathroom with terrazzo tile, frameless glass shower, and wood slat accent wall',
      alt: 'Calgary luxury bathroom renovation with terrazzo tile and spa features'
    },
    {
      id: 'b2',
      image: '/gallery/bathroom-terrazzo-shower.webp',
      title: 'Terrazzo Rainfall Shower',
      description: 'Walk-in shower with terrazzo tile walls and premium rainfall shower head',
      alt: 'Calgary bathroom renovation with terrazzo shower and rainfall head'
    },
    {
      id: 'b3',
      image: '/gallery/bathroom-herringbone.webp',
      title: 'Black Herringbone Shower',
      description: 'Modern shower with black herringbone tile and brass fixtures',
      alt: 'Calgary bathroom renovation with black herringbone tile shower'
    },
    {
      id: 'b4',
      image: '/gallery/bathroom-marble-master.webp',
      title: 'Marble Master Bathroom',
      description: 'Complete bathroom renovation with marble tile flooring, freestanding soaker tub, and custom vanity',
      alt: 'Calgary bathroom renovation with marble tile and freestanding tub'
    },
    {
      id: 'b5',
      image: '/gallery/bathroom-modern-glass.webp',
      title: 'Modern Glass Shower Enclosure',
      description: 'Clean-lined shower with glass enclosure and vertical tile pattern',
      alt: 'Calgary modern bathroom with glass shower enclosure'
    },
    {
      id: 'b6',
      image: '/gallery/bathroom-terrazzo-vanity.webp',
      title: 'Terrazzo Vanity with Brass Fixtures',
      description: 'Modern bathroom vanity featuring dark terrazzo countertop, integrated sink, and brushed brass fixtures',
      alt: 'Calgary bathroom renovation with terrazzo vanity and brass fixtures'
    },
    {
      id: 'b7',
      image: '/gallery/bathroom-black-marble.webp',
      title: 'Black Marble Master Bathroom',
      description: 'Stunning master bathroom with black marble flooring, soaker tub, and walk-in shower',
      alt: 'Calgary luxury bathroom with black marble flooring and modern fixtures'
    },
  ];

  // FIREPLACES - Secondary Focus (Strongest First)
  const fireplaceImages = [
    {
      id: 'f1',
      image: '/gallery/fireplace-gold-trim.webp',
      title: 'Elegant Gold-Trim Fireplace',
      description: 'Sophisticated marble fireplace with gold trim detailing and ornate mirror',
      alt: 'Custom fireplace Calgary with marble surround and gold accents'
    },
    {
      id: 'f2',
      image: '/gallery/fireplace-modern-marble.webp',
      title: 'Modern Marble Feature Wall',
      description: 'Floor-to-ceiling marble tile feature wall with linear electric fireplace',
      alt: 'Calgary basement feature wall with marble tile and modern linear fireplace'
    },
    {
      id: 'f3',
      image: '/gallery/fireplace-rustic-stone.webp',
      title: 'Rustic Stone Fireplace',
      description: 'Floor-to-ceiling natural stone fireplace with reclaimed wood mantel',
      alt: 'Custom fireplace Calgary with natural stone surround and wood beam mantel'
    },
    {
      id: 'f4',
      image: '/gallery/fireplace-contemporary.webp',
      title: 'Contemporary Living Space',
      description: 'Open concept living space with modern fireplace and premium terrazzo flooring',
      alt: 'Calgary basement renovation with modern fireplace and large windows'
    },
  ];

  // RECENT PROJECTS IN CALGARY (Featured Section)
  const featuredProjects = [
    {
      id: 'p1',
      title: 'Custom Bathroom Renovation',
      location: 'Southwest Calgary',
      images: ['/gallery/bathroom-luxury-spa.webp', '/gallery/bathroom-terrazzo-shower.webp'],
      description: 'Complete spa-style bathroom transformation featuring terrazzo tile, frameless glass shower, and premium fixtures.'
    },
    {
      id: 'p2',
      title: 'Modern Fireplace Installation',
      location: 'Northwest Calgary',
      images: ['/gallery/fireplace-gold-trim.webp', '/gallery/fireplace-modern-marble.webp'],
      description: 'Elegant fireplace feature walls with marble surrounds and contemporary linear designs.'
    },
    {
      id: 'p3',
      title: 'Custom Feature Installation',
      location: 'Calgary',
      images: ['/gallery/custom-pizza-oven.webp', '/gallery/custom-arched-bar.webp', '/gallery/custom-kitchen-marble.webp'],
      description: 'Unique custom installations including built-in entertainment features, artisan finishing, and premium kitchen upgrades.'
    },
  ];

  // WORK IN PROGRESS - Minimal (Bottom of page)
  const wipImages = [
    {
      id: 'w1',
      image: '/gallery/wip-vaulted-ceiling.webp',
      title: 'Vaulted Ceiling Installation',
      description: 'Custom vaulted ceiling with exposed wooden beams'
    },
    {
      id: 'w2',
      image: '/gallery/wip-drywall-finishing.webp',
      title: 'Professional Drywall Finishing',
      description: 'Precision drywall installation and finishing work'
    },
    {
      id: 'w3',
      image: '/gallery/wip-terrazzo-shower.webp',
      title: 'Terrazzo Shower in Progress',
      description: 'Custom terrazzo tile shower installation'
    },
    {
      id: 'w4',
      image: '/gallery/wip-floor-leveling.webp',
      title: 'Self-Leveling Floor Installation',
      description: 'Professional floor preparation with self-leveling compound'
    },
    {
      id: 'w5',
      image: '/gallery/wip-bathroom-tiling.webp',
      title: 'Bathroom Tile Installation',
      description: 'Expert tile installation for shower and flooring'
    },
  ];

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
  };

  // Gallery Section Component
  const GallerySection = ({ title, subtitle, images, showCTA = false }) => (
    <section className="py-16 md:py-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-3">
            {subtitle}
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a]">
            {title}
          </h2>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {images.map((item, index) => (
            <div
              key={item.id}
              className="group relative aspect-[4/3] overflow-hidden cursor-pointer bg-slate-100"
              onClick={() => openLightbox(images, index)}
              data-testid={`gallery-item-${item.id}`}
            >
              <img
                src={item.image}
                alt={item.alt || item.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/80 via-[#0f172a]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-heading text-lg font-semibold text-white">
                    {item.title}
                  </h3>
                  <p className="text-slate-300 text-sm mt-1 line-clamp-2">
                    {item.description}
                  </p>
                </div>
              </div>
              {/* Zoom indicator */}
              <div className="absolute top-3 right-3 bg-white/90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                <Camera className="w-4 h-4 text-[#0f172a]" />
              </div>
            </div>
          ))}
        </div>

        {showCTA && (
          <div className="text-center mt-12">
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-[#0f172a] text-white px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-[#1e293b] transition-colors"
              data-testid="gallery-section-cta"
            >
              Get Your Free Estimate
              <ArrowRight size={18} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div className="min-h-screen" data-testid="gallery-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
            Our Portfolio
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Real Projects Completed in Calgary
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            Browse our collection of completed renovation projects across Calgary and surrounding areas.
            Quality craftsmanship for Calgary homes.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-[#c5a059] text-[#0f172a] px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white transition-colors"
            data-testid="gallery-hero-cta"
          >
            Get Your Free Estimate
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* BATHROOMS - Primary Focus */}
      <div className="bg-white">
        <GallerySection 
          title="Bathroom Renovations"
          subtitle="Our Specialty"
          images={bathroomImages}
          showCTA={true}
        />
      </div>

      {/* FIREPLACES - Secondary Focus */}
      <div className="bg-[#f8f9fa]">
        <GallerySection 
          title="Fireplace Installations"
          subtitle="Feature Walls & Fireplaces"
          images={fireplaceImages}
        />
      </div>

      {/* RECENT PROJECTS IN CALGARY - Featured Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-3">
              Featured Work
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a]">
              Recent Projects in Calgary
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {featuredProjects.map((project) => (
              <div 
                key={project.id}
                className="bg-[#f8f9fa] overflow-hidden group"
                data-testid={`featured-project-${project.id}`}
              >
                {/* Project Images */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={project.images[0]}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {project.images.length > 1 && (
                    <div className="absolute bottom-3 right-3 bg-[#0f172a]/80 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5">
                      <Camera size={12} />
                      {project.images.length} Photos
                    </div>
                  )}
                </div>
                {/* Project Info */}
                <div className="p-6">
                  <p className="text-[#c5a059] text-xs uppercase tracking-wider mb-2">
                    {project.location}
                  </p>
                  <h3 className="font-heading text-xl font-semibold text-[#0f172a] mb-3">
                    {project.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {project.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-14">
            <p className="text-slate-600 mb-6 max-w-xl mx-auto">
              Ready to see your home transformed with the same quality and attention to detail?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-3 bg-[#0f172a] text-white px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-[#1e293b] transition-colors"
                data-testid="featured-cta-button"
              >
                Start Your Project
                <ArrowRight size={18} />
              </Link>
              <a
                href="tel:403-402-6596"
                className="inline-flex items-center justify-center gap-3 border-2 border-[#0f172a] text-[#0f172a] px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-[#0f172a] hover:text-white transition-colors"
                data-testid="featured-call-button"
              >
                Call 403-402-6596
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WORK IN PROGRESS - Minimal Section at Bottom */}
      <section className="py-14 md:py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="text-slate-500 text-xs uppercase tracking-[0.2em] mb-2">
              Behind the Scenes
            </p>
            <h2 className="font-heading text-2xl font-semibold text-slate-700">
              Work In Progress
            </h2>
            <p className="text-slate-500 text-sm mt-2 max-w-lg mx-auto">
              A glimpse into our craftsmanship and attention to detail during the renovation process.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {wipImages.map((item, index) => (
              <div
                key={item.id}
                className="relative aspect-[4/3] overflow-hidden cursor-pointer group"
                onClick={() => openLightbox(wipImages, index)}
                data-testid={`wip-item-${item.id}`}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 grayscale-[20%]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="font-heading text-sm font-semibold text-white">
                      {item.title}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 md:py-24 bg-[#0f172a]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Get inspired by our work and let's discuss how we can create something amazing for your home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-3 bg-[#c5a059] text-[#0f172a] px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white transition-colors"
              data-testid="gallery-cta-button"
            >
              Request Free Estimate
              <ArrowRight size={18} />
            </Link>
            <a
              href="tel:403-402-6596"
              className="inline-flex items-center justify-center gap-3 border-2 border-white text-white px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white hover:text-[#0f172a] transition-colors"
              data-testid="gallery-call-button"
            >
              Call 403-402-6596
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && lightboxImages.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="lightbox-modal"
        >
          {/* Close button */}
          <button 
            className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors z-50"
            onClick={closeLightbox}
            aria-label="Close lightbox"
            data-testid="lightbox-close"
          >
            <X size={32} />
          </button>

          {/* Navigation arrows */}
          {lightboxImages.length > 1 && (
            <>
              <button 
                className="absolute left-4 md:left-8 text-white/80 hover:text-white transition-colors z-50 p-2"
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                aria-label="Previous image"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={40} />
              </button>
              <button 
                className="absolute right-4 md:right-8 text-white/80 hover:text-white transition-colors z-50 p-2"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                aria-label="Next image"
                data-testid="lightbox-next"
              >
                <ChevronRight size={40} />
              </button>
            </>
          )}

          {/* Image */}
          <div 
            className="max-w-[90vw] max-h-[85vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImages[lightboxIndex].image}
              alt={lightboxImages[lightboxIndex].alt || lightboxImages[lightboxIndex].title}
              className="max-w-full max-h-[85vh] object-contain"
            />
            {/* Caption */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
              <h3 className="font-heading text-xl font-semibold text-white">
                {lightboxImages[lightboxIndex].title}
              </h3>
              {lightboxImages[lightboxIndex].description && (
                <p className="text-slate-300 text-sm mt-1">
                  {lightboxImages[lightboxIndex].description}
                </p>
              )}
            </div>
          </div>

          {/* Image counter */}
          {lightboxImages.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
              {lightboxIndex + 1} / {lightboxImages.length}
            </div>
          )}
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;
